// Liquid supplies Shopify's fingerprinted dependency URL so profile upgrades do
// not reuse a cached, unversioned rules module from an earlier preview visit.
const coreUrl = document.querySelector('script[data-ksl-profile-core]')?.dataset.kslProfileCore || new URL('./ksl-personalization-core.js',import.meta.url).href;
const { configurationError, available, visible, itemCounts, reconcile, validate, properties, fileError, directLocketProfile, resolveVariantPositions, isChainOption } = await import(coreUrl);
const nativeOptionsUrl=document.querySelector('script[data-ksl-profile-core]')?.dataset.kslNativeOptions||new URL('./ksl-native-options.js',import.meta.url).href;
const {nativeOptionProfile,nativeOptionControl,syncNativeOptions,requiresFullVariantList}=await import(nativeOptionsUrl);

class KslPersonalization extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.urls = [];
    this.state = { active:false, positions:{}, items:[], count:0 };
    this.errors = [];
    this.confirmed = false;
    // The block may occur before its associated form in the section.
    this.frame = requestAnimationFrame(() => this.initialize());
  }
  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.controller?.abort(); this.controller = null;
    this.observer?.disconnect();
    this.revoke();
    this.loadedFonts?.forEach(font => document.fonts.delete(font)); this.loadedFonts = [];
  }
  t(key, values = {}) {
    return String(this.config?.messages?.[key] || key).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? '');
  }
  node(tag, text, className) {
    const el = document.createElement(tag);
    if (text != null) el.textContent = text;
    if (className) el.className = className;
    return el;
  }
  listen(el, event, fn) { el.addEventListener(event, fn, {signal:this.controller.signal}); }
  async initialize() {
    const loadController = this.controller;
    this.form = document.getElementById(this.dataset.formId);
    this.message = this.querySelector('[data-profile-message]');
    this.fields = this.querySelector('[data-profile-fields]');
    this.summary = this.querySelector('[data-profile-summary]');
    this.output = this.querySelector('[data-profile-properties]');
    try { this.config = JSON.parse(this.querySelector('[data-config]').content.textContent); } catch { return this.block(); }
    this.setVariantContext(this.config.selected_variant);
    this.profile = structuredClone(this.config.profile);
    if ([3,4].includes(this.profile?.schema_version) || this.config.personalization_only) {
      try {
        const url = document.querySelector('script[data-ksl-profile-core]')?.dataset.kslProfileLibrary || new URL('./ksl-profile-library.js',import.meta.url).href;
        const {compileStructuredProfile,compileReusableProfile,withProductOptions} = await import(url);
        if (this.controller !== loadController || loadController.signal.aborted || !this.isConnected) return;
        if (this.profile?.schema_version === 3) this.profile = compileStructuredProfile(this.profile).configuration;
        if (this.profile?.schema_version === 4) {
          const compiled=compileReusableProfile(this.profile);
          if(!compiled.ready)return this.block();
          this.profile=compiled.configuration;
        }
        if (this.config.personalization_only) this.profile = withProductOptions(this.profile,this.config.product_options_profile);
      } catch { return this.block(); }
    }
    this.profile=nativeOptionProfile(this.profile,this.config.option_names||[]);
    try { this.profile = resolveVariantPositions(this.profile,this.config.variants,this.config.option_names); } catch { return this.block(); }
    if (this.config.direct_personalization) this.profile = directLocketProfile(this.profile);
    if (this.profile?.package_rules?.inferred) this.profile.instructions = this.t('direct_instructions');
    // A merchant-supplied locket chart applies to typed engraving only, never chain notes
    // or uploaded handwriting. Existing per-profile font libraries take precedence.
    if (this.config.reference_libraries?.font && this.profile?.positions) {
      for (const position of this.profile.positions) for (const mode of position.modes || []) {
        if (mode.kind === 'text' && mode.id === 'text' && position.id !== 'chain' && !mode.fonts) {
          mode.fonts = [{id:'classic',label:'Classic Font'},{id:'muse',label:'Muse Font'},{id:'celine',label:'Celine Font'},{id:'monogram',label:'Monogram'}];
        }
      }
    }
    if (!this.form || configurationError(this.profile) || this.profile.required && !this.profile.enabled || this.config.variants_complete === false && requiresFullVariantList(this.profile) || this.config.required === true && (!this.profile.enabled || !this.profile.required)) return this.block();
    this.form.enctype = 'multipart/form-data';
    this.form.dataset.kslProfile = 'true';
    this.form.addEventListener('submit', event => {
      if (!this.prepare()) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, {capture:true, signal:this.controller.signal});
    // Native File objects are appended to the *associated* form only. Never blob/data URLs.
    this.listen(this.form, 'formdata', event => {
      if (!this.initialized || validate(this.profile,this.variant,this.state,this.confirmed).length) return;
      for (const [key,value] of Object.entries(properties(this.profile,this.variant,this.state))) {
        if (value instanceof File) event.formData.append(`properties[${key}]`,value,value.name);
      }
    });
    const idInput = this.form.querySelector('[name=id]');
    this.listen(idInput, 'change', () => this.selectVariant());
    this.listen(this, 'change', event => {
      if (event.target.closest('ksl-reference-picker')) {
        this.clearConfirmation();
        this.clearOutput(); this.updateSummary();
      }
    });
    this.observer = new MutationObserver(() => this.selectVariant());
    this.observer.observe(idInput,{attributes:true,attributeFilter:['value','disabled']});
    this.initialized = true;
    this.querySelector('[data-profile-support]')?.setAttribute('hidden', '');
    this.selectVariant();
  }
  block() {
    this.initialized = false;
    this.querySelector('[data-profile-support]')?.removeAttribute('hidden');
    if (this.message) this.message.textContent = this.t('configuration');
    this.form?.querySelectorAll('[name=add]').forEach(b => {b.disabled = true;});
    this.fields?.replaceChildren(); this.output?.replaceChildren();
  }
  selectVariant() {
    if (!this.initialized) return this.block();
    const next = this.config.variants.find(v => String(v.id) === this.form.querySelector('[name=id]')?.value);
    if (this.variant?.id === next?.id && this.variant !== undefined) return this.syncButton();
    const hadVariant = !!this.variant;
    this.variant = next;
    this.state = reconcile(this.profile,this.variant,this.state);
    if(this.variant)try{this.state=syncNativeOptions(this.profile,this.variant,this.config.option_names||[],this.state,available);}catch{return this.block();}
    this.confirmed = false;
    this.errors = [];
    this.message.textContent = !next?.available ? this.t('unavailable') : '';
    this.render(); this.syncButton();
    if (hadVariant) this.announce(this.t('variant_changed'));
  }
  syncButton() {
    this.form?.querySelectorAll('[name=add]').forEach(b => { b.disabled = !this.initialized || !this.variant?.available; });
  }
  setVariantContext(context) {
    if(!context?.id||!Array.isArray(context.option_values)||!this.config)return;
    const index=this.config.variants.findIndex(v=>String(v.id)===String(context.id));
    if(index<0)this.config.variants.push(context);else this.config.variants[index]=context;
  }
  announce(text) { this.status.textContent = text; }
  revoke() { this.urls?.forEach(url => URL.revokeObjectURL(url)); this.urls = []; }
  id(key) { return `${this.dataset.formId}-personalization-${key}`; }
  field(parent,key,title,control,hint) {
    const wrapper = this.node('div',null,'ksl-personalization__field');
    control.id = this.id(key); control.setAttribute('form',this.dataset.formId);
    const lab = this.node('label',title); lab.htmlFor = control.id;
    const help = this.node('small',hint || ''); help.id = `${control.id}-hint`;
    const error = this.node('small','', 'ksl-personalization__error'); error.id = `${control.id}-error`; error.dataset.errorFor = key;
    control.setAttribute('aria-describedby',`${help.id} ${error.id}`); control.dataset.field = key;
    if(control.type === 'checkbox') {lab.prepend(control); wrapper.append(lab,help,error);}
    else wrapper.append(lab,control,help,error);
    parent.append(wrapper);
    return wrapper;
  }
  select(parent,key,title,options,value,changed,hint,omitEmpty=false,placeholder='choose') {
    const select = this.node('select');
    const choices = options.filter(x => available(x,this.variant,this.state));
    if (!omitEmpty || !choices.length) { const empty = this.node('option',this.t(omitEmpty?'unavailable':placeholder)); empty.value = ''; select.append(empty); }
    if (omitEmpty && !choices.length) select.disabled = true;
    choices.forEach(option => { const o = this.node('option',option.label); o.value = option.id; select.append(o); });
    select.value = value || '';
    this.field(parent,key,title,select,hint);
    this.listen(select,'change', () => { this.confirmed = false; changed(select.value); this.showErrors(false); });
    return select;
  }
  text(parent,key,title,value,limits,changed,hint,example) {
    const input = this.node('textarea'); input.rows = Math.min(limits.lines || 3,5); input.value = value || '';
    if (example) input.placeholder = example;
    this.field(parent,key,title,input,hint);
    const count = this.node('small'); count.setAttribute('aria-live','off'); input.after(count);
    count.hidden = !Object.keys(limits).length;
    const updateCount = () => { if (!count.hidden) count.textContent = this.t('counter',{count:Array.from(input.value).length, limits:Object.entries(limits).map(([k,v])=>`${v} ${this.t(k)}`).join(' · ')}); };
    updateCount();
    this.listen(input,'input', () => { changed(input.value); this.confirmed = false; this.clearConfirmation(); updateCount(); this.updateSummary(); this.clearOutput(); this.showErrors(false); });
  }
  render() {
    if (this.profile.package_rules?.inferred) this.state = reconcile(this.profile,this.variant,this.state);
    this.revoke(); this.fields.replaceChildren(); this.clearOutput();
    this.querySelectorAll(':scope > .ksl-personalization__confirmation').forEach(el=>el.remove()); this.details = null;
    this.status = this.node('p',''); this.status.setAttribute('role','status'); this.fields.append(this.status);
    const options = this.node('div',null,'ksl-personalization__options'); this.fields.append(options);
    for(const option of this.profile.options || []) {
      if (this.profile.package_rules?.inferred && option.id === this.profile.package_rules.option) continue;
      const nativeControl=nativeOptionControl(this.profile,option,this.variant,this.config.option_names||[],this.state,available);
      this.select(options,`option_${option.id}`,nativeControl?.label||option.label,nativeControl?.choices||option.values,this.state.options?.[option.id],value=>{
        this.state.options ||= {}; this.state.options[option.id]=value;
        this.state=reconcile(this.profile,this.variant,this.state); this.confirmed=false; this.render();
        this.querySelector(`[data-field="option_${option.id}"]`)?.focus();
      },option.instructions,isChainOption(option));
    }
    if (!this.profile.required) {
      const toggle = this.node('input'); toggle.type='checkbox'; toggle.checked=this.state.active;
      this.field(this.fields,'active',this.t('opt_in'),toggle).classList.add('ksl-personalization__activation');
      this.listen(toggle,'change', () => {this.state.active=toggle.checked; this.confirmed=false; this.render(); this.querySelector('[data-field=active]')?.focus();});
    }
    if (!this.state.active && !this.profile.required) { this.updateSummary(); return; }
    this.details = this.node('section',null,'ksl-personalization__details');
    this.details.setAttribute('aria-labelledby',this.id('details_heading'));
    const heading = this.node('h2',this.t('details_heading'),'ksl-personalization__section-title'); heading.id=this.id('details_heading');
    this.details.append(heading); this.fields.append(this.details);
    if (this.profile.instructions) this.details.append(this.node('p',this.profile.instructions,'ksl-personalization__instructions'));
    if (this.profile.positions.length && !this.profile.positions.some(pos=>visible(pos,this.variant,this.state))) {
      this.details.append(this.node('p',this.t('upload_guide'),'ksl-personalization__guide'));
    }
    let positionIndex = 0;
    for (const pos of this.profile.positions) {
      if (!visible(pos,this.variant,this.state)) continue;
      const required = this.positionRequired(pos);
      const group = this.positionGroup(pos,++positionIndex,required); this.details.append(group);
      const entry = this.state.positions[pos.id] ||= {mode:''};
      const types = this.select(group,pos.id,this.t('content_label'),pos.modes,entry.mode,value => {
        this.state.positions[pos.id] = {mode:value}; this.state=reconcile(this.profile,this.variant,this.state); this.render(); this.querySelector(`[data-field="${pos.id}"]`)?.focus();
      }, pos.instructions,false,required?'choose_content':'leave_blank');
      if (required) types.setAttribute('aria-required','true');
      const mode = pos.modes.find(m=> m.id===entry.mode && available(m,this.variant,this.state));
      if (!mode) {
        const photo = pos.modes.find(m=>m.kind==='photo' && available(m,this.variant,this.state));
        if (photo) {
          const shortcut=this.node('button',this.t('upload_photo'),'button button--secondary ksl-personalization__upload-button'); shortcut.type='button'; group.append(shortcut);
          this.listen(shortcut,'click',()=>{
            this.state.positions[pos.id]={mode:photo.id}; this.confirmed=false; this.render();
            const input=this.querySelector('[data-field="'+pos.id+'_file"]'); input?.focus(); input?.click();
          });
        }
        continue;
      }
      if (mode.kind === 'none') continue;
      if (mode.kind === 'text') this.text(group,`${pos.id}_text`,mode.text_label || this.t('engraving'),entry.text,mode.limits,value=>{entry.text=value;},mode.instructions,mode.example);
      else this.upload(group,pos,mode,entry);
      for (const [key,lib] of [['font','fonts'],['design','designs'],['symbol','symbols']]) {
        if (!mode[lib]) continue;
        const select = this.select(group,`${pos.id}_${key}`,this.t(key),mode[lib],entry[key],value=>{entry[key]=value; this.clearConfirmation(); this.clearOutput(); this.updateSummary(); this.updateSample(select,mode[lib],entry[key],key);});
        const sample = this.node('div'); sample.dataset.sample = key; select.parentElement.append(sample);
        this.updateSample(select,mode[lib],entry[key],key);
      }
    }
    if (this.profile.items && visible(this.profile.items,this.variant,this.state)) this.renderItems(positionIndex+1);
    if (this.form.elements.namedItem('quantity')) {
      const quantity = this.node('p',this.t('quantity_hint'),'ksl-personalization__quantity-hint'); this.fields.append(quantity);
    }
    const confirm = this.node('input'); confirm.type='checkbox'; confirm.checked=this.confirmed;
    this.confirmation = this.node('div',null,'ksl-personalization__confirmation'); this.summary.after(this.confirmation);
    this.field(this.confirmation,'confirm',this.t('confirm'),confirm);
    this.listen(confirm,'change',()=>{this.confirmed=confirm.checked; this.clearOutput(); this.showErrors(false);});
    this.updateSummary(); this.showErrors(false);
  }
  positionRequired(pos) {
    if (pos.required) return true;
    const rules=this.profile.package_rules;
    if (!rules || rules.inferred) return false;
    const rule=rules.values[this.state.options?.[rules.option]];
    if (!rule) return false;
    const candidates=kind=>this.profile.positions.filter(p=>visible(p,this.variant,this.state)&&p.modes.some(m=>available(m,this.variant,this.state)&&kind(m.kind)));
    const photos=candidates(kind=>kind==='photo');
    const engraving=candidates(kind=>kind!=='photo'&&kind!=='none');
    return rule.photo_count>0&&rule.photo_count===photos.length&&photos.some(p=>p.id===pos.id) || rule.engraving_required&&engraving.length===1&&engraving[0].id===pos.id;
  }
  positionGroup(spec,index,required=spec.required) {
    const group=this.node('fieldset',null,'ksl-personalization__position'); group.setAttribute('aria-label',spec.label);
    const legend=this.node('legend');
    const number=this.node('span',String(index).padStart(2,'0'),'ksl-personalization__position-number'); number.setAttribute('aria-hidden','true');
    legend.append(number,this.node('span',spec.label,'ksl-personalization__position-title'),this.node('span',this.t(required?'position_required':'position_optional'),'ksl-personalization__position-status'));
    group.append(legend); return group;
  }
  updateSample(select,library,id,key) {
    const sample = select.parentElement.querySelector('[data-sample]'); if (!sample) return;
    sample.replaceChildren();
    const option = library.find(x=>x.id===id); if (!option) return;
    // Only actual merchant font files/images are shown; labels alone never fake a preview.
    if (key === 'font' && /^https:\/\/cdn\.shopify\.com\//.test(option.asset_url || '') && /\.(woff2?|ttf|otf)(\?|$)/i.test(option.asset_url)) {
      const family = `ksl-${this.dataset.formId.replace(/[^a-z0-9]/gi,'')}-${option.id}`;
      const font = new FontFace(family,`url(${JSON.stringify(option.asset_url)})`);
      font.load().then(loaded=>{ if (!this.isConnected || select.value!==id) return; document.fonts.add(loaded); this.loadedFonts ||= []; this.loadedFonts.push(loaded); const text=this.node('span',option.sample || option.label,'ksl-personalization__sample'); text.style.fontFamily=family; sample.replaceChildren(text); }).catch(()=>{ sample.textContent=this.t('sample_unavailable'); });
    }
    if (key === 'design' && /^https:\/\/cdn\.shopify\.com\//.test(option.image_url || '')) { const img=this.node('img'); img.src=option.image_url; img.alt=option.label; img.className='ksl-personalization__design'; sample.append(img,this.node('small',this.t('illustration'))); }
  }
  upload(group,pos,mode,entry) {
    const input = this.node('input'); input.type='file'; input.accept=mode.upload.mime_types.join(','); input.multiple=mode.upload.max_files>1;
    const wrapper = this.field(group,`${pos.id}_file`,this.t(mode.kind === 'photo' ? 'photo' : 'artwork'),input,this.t('upload_hint',{types:mode.upload.mime_types.map(x=>x.split('/')[1]).join(', '),size:Math.round(mode.upload.max_bytes/1024/1024*10)/10,count:mode.upload.max_files}));
    wrapper.classList.add('ksl-personalization__upload');
    const remove=this.node('button',this.t('remove_file'),'ksl-personalization__remove'); remove.type='button';
    const status=this.node('small',this.t('upload_pending')); status.setAttribute('role','status'); wrapper.append(status);
    const previews=this.node('div',null,'ksl-personalization__files'); wrapper.append(previews);
    const draw=()=> {
      previews.replaceChildren();
      for (const file of entry.files || []) {
        const detail=this.node('div'); detail.append(this.node('small',file.name));
        if (file.type.startsWith('image/')) { const img=this.node('img'); const url=URL.createObjectURL(file); this.urls.push(url); img.src=url; img.alt=this.t('local_preview'); detail.append(img); }
        previews.append(detail);
      }
      remove.hidden=!entry.files?.length;
      status.textContent=entry.files?.length ? this.t('file_ready') : this.t('upload_pending');
    };
    draw();
    this.listen(input,'change',()=> {
      this.confirmed=false; this.clearConfirmation(); this.clearOutput();
      const files=Array.from(input.files);
      const error=fileError(files,mode.upload,false);
      entry.files=error ? [] : files;
      if(error) input.value='';
      draw(); status.textContent=error ? this.t(error) : files.length ? this.t('file_ready') : this.t('upload_pending');
      this.updateSummary(); this.showErrors(false);
    });
    wrapper.append(remove);
    this.listen(remove,'click',()=>{entry.files=[]; input.value=''; this.confirmed=false; this.clearConfirmation(); this.clearOutput(); draw(); this.updateSummary(); this.showErrors(false); input.focus();});
  }
  renderItems(index=1) {
    const spec=this.profile.items, group=this.positionGroup(spec,index); (this.details || this.fields).append(group);
    this.select(group,'count',this.t('count'),itemCounts(spec,this.variant,this.state).map(n=>({id:String(n),label:String(n)})),String(this.state.count),value=> {
      const count=Number(value); const removed=count<this.state.count;
      this.state.count=count; this.state.items=this.state.items.slice(0,count);
      while(this.state.items.length<count) this.state.items.push({});
      this.render(); if(removed) this.announce(this.t('items_removed')); this.querySelector('[data-field=count]')?.focus();
    });
    this.state.items.forEach((item,i)=>{
      const sub=this.node('fieldset',null,'ksl-personalization__item'); sub.append(this.node('legend',`${spec.label} ${i+1}`)); group.append(sub);
      if (spec.alphabet === 'latin_upper') this.select(sub,`item_${i}_name`,`${spec.label} ${i+1}`,Array.from({length:26},(_,n)=>({id:String.fromCharCode(65+n),label:String.fromCharCode(65+n)})),item.name,value=>{item.name=value;this.clearConfirmation();this.clearOutput();this.updateSummary();});
      else this.text(sub,`item_${i}_name`,this.t('name'),item.name,spec.limits,value=>{item.name=value;},spec.instructions,spec.example);
      for(const [key,lib] of [['font','fonts'],['birthstone','birthstones']]) if(spec[lib]) {
        const select=this.select(sub,`item_${i}_${key}`,this.t(key),spec[lib],item[key],value=>{item[key]=value; this.clearConfirmation(); this.clearOutput(); this.updateSummary(); this.updateSample(select,spec[lib],value,key);});
        const sample=this.node('div'); sample.dataset.sample=key; select.parentElement.append(sample); this.updateSample(select,spec[lib],item[key],key);
      }
    });
  }
  clearConfirmation() { this.confirmed=false; const input=this.querySelector('[data-field=confirm]'); if(input) input.checked=false; }
  clearOutput() { this.output?.replaceChildren(); }
  referenceProperties() {
    return Object.fromEntries([...this.querySelectorAll('[data-stone-fields] select, [data-stone-chart-property]')]
      .filter(control => !control.disabled && control.value && /^properties\[.+\]$/.test(control.name))
      .map(control => [control.name.slice(11,-1),control.value]));
  }
  updateSummary() {
    this.summary.replaceChildren();
    if(this.dataset.hideSummary!=null) {this.summary.hidden=true;return;}
    // Only omit repeated physical choices. Placement, source fonts and other
    // custom option labels still belong in the customer's production review.
    const physicalChoice = /^(?:finish|colou?r|material|metal|(?:ring\s+)?size|(?:(?:necklace|chain|bracelet)\s+)?length|chain(?:\s+(?:option|style|type))?)$/i;
    const optionLabels = new Set((this.profile.options || []).filter(o=>o.id!==this.profile.package_rules?.option&&physicalChoice.test(o.label.trim())).map(o=>o.label));
    const rows = Object.entries({...properties(this.profile,this.variant,this.state),...this.referenceProperties()}).filter(([key,value])=>!key.startsWith('_')&&!optionLabels.has(key)&&value!=null&&value!=='');
    this.summary.hidden = !rows.length || !this.state.active && !this.profile.required;
    if(this.summary.hidden)return;
    const header=this.node('div',null,'ksl-personalization__review-heading');
    header.append(this.node('h3',this.t('summary'),'ksl-personalization__section-title'),this.node('p',`${this.t('price')}: ${this.variant?.price || this.t('unavailable')}`)); this.summary.append(header);
    const list=this.node('dl',null,'ksl-personalization__review-list'); this.summary.append(list);
    for(const [key,value] of rows) {
      const label = this.profile.package_rules?.inferred && key === this.profile.options.find(o=>o.id===this.profile.package_rules.option)?.label ? this.t('personalization_label') : key;
      const position=this.profile.positions.find(pos=>key===`${pos.label} type`);
      const mode=position?.modes.find(m=>m.id===this.state.positions[position.id]?.mode);
      const display=mode?.label || (value instanceof File ? value.name : String(value));
      const row=this.node('div'); row.append(this.node('dt',`${label}: `),this.node('dd',display)); list.append(row);
    }
  }
  showErrors(focus) {
    if(this.attempted) this.errors=validate(this.profile,this.variant,this.state,this.confirmed);
    this.querySelectorAll('[data-error-for]').forEach(e=>{e.textContent='';});
    this.querySelectorAll('[aria-invalid]').forEach(e=>e.removeAttribute('aria-invalid'));
    for(const error of this.errors) {
      const field=this.querySelector(`[data-field="${error.field}"]`), message=this.querySelector(`[data-error-for="${error.field}"]`);
      if(field) field.setAttribute('aria-invalid','true');
      if(message) message.textContent=this.t(['characters','words','lines'].includes(error.code) ? `${error.code}_error` : error.code,{count:error.count});
    }
    if(focus && this.errors.length) {
      const first=this.querySelector(`[data-field="${this.errors[0].field}"]`);
      (first?.closest('.ksl-choice')?.querySelector('[role=radio][tabindex="0"]') || first || this.message).focus();
      this.announce(this.t('review_errors'));
    }
    if(!this.errors.length && this.status?.textContent===this.t('review_errors')) this.status.textContent='';
  }
  prepare() {
    if(!this.initialized) {this.block(); this.message?.focus(); return false;}
    this.selectVariant();
    this.attempted=true;
    this.errors=validate(this.profile,this.variant,this.state,this.confirmed);
    this.clearOutput(); this.showErrors(true);
    if(this.errors.length) return false;
    const values=properties(this.profile,this.variant,this.state);
    if(this.config.profile?.schema_version===4)Object.assign(values,{_ksl_profile:this.profile.id,_ksl_profile_version:String(this.profile.version),_ksl_personalized:this.state.active?'true':'false'});
    const snapshot={schema_version:2,profile:this.profile.id,version:this.profile.version,reference:this.config.profile_ref,product:this.config.product_id,variant:String(this.variant.id),selections:{...Object.fromEntries(Object.entries(values).filter(([,v])=>!(v instanceof File))),...this.referenceProperties()}};
    if(Object.keys(values).length) values._ksl_production=JSON.stringify(snapshot);
    // Public labels preserve production choices even if a profile is edited later.
    for(const [key,value] of Object.entries(values)) {
      if(value instanceof File) continue;
      const input=this.node('input'); input.type='hidden'; input.name=`properties[${key}]`; input.value=value; input.setAttribute('form',this.dataset.formId); this.output.append(input);
    }
    return true;
  }
}
if(!customElements.get('ksl-personalization')) customElements.define('ksl-personalization',KslPersonalization);
